import { useState, useEffect, useCallback } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useTranslation } from 'react-i18next';

const AUTO_INTERVAL = 5000;

export default function PillarDeepDive() {
  const [active, setActive] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [mobileVisible, setMobileVisible] = useState(true);
  const [progressKey, setProgressKey] = useState(0);
  const { ref: headerRef, isVisible: hv } = useScrollAnimation();
  const { t } = useTranslation();

  const deepDives = [
    {
      index: '01', category: 'FOCUS',
      label: t('dd_1_label'), title: t('dd_1_title'),
      quote: t('dd_1_quote'), highlight: t('dd_1_highlight'),
      stat: { value: t('dd_1_stat'), desc: t('dd_1_stat_desc'), valueClass: 'text-3xl' },
      image: 'https://static.readdy.ai/image/7c44e70ab9ad63a75c237184bae01347/36fdfca51ed49ee077396e636019e003.jpeg',
    },
    {
      index: '02', category: 'PRIVACY',
      label: t('dd_2_label'), title: t('dd_2_title'),
      quote: t('dd_2_quote'), highlight: t('dd_2_highlight'),
      stat: { value: t('dd_2_stat'), desc: t('dd_2_stat_desc') },
      image: 'https://static.readdy.ai/image/7c44e70ab9ad63a75c237184bae01347/d5a57854bef21b4c72a74b1f00834824.jpeg',
    },
    {
      index: '03', category: 'COMFORT',
      label: t('dd_3_label'), title: t('dd_3_title'),
      quote: t('dd_3_quote'), highlight: t('dd_3_highlight'),
      stat: { value: t('dd_3_stat'), desc: t('dd_3_stat_desc') },
      image: 'https://static.readdy.ai/image/7c44e70ab9ad63a75c237184bae01347/f436ac091e069aa12bbfd89aab730dc2.jpeg',
    },
  ];

  const switchTo = useCallback((next: number) => {
    setMobileVisible(false);
    setTimeout(() => { setActive(next); setProgressKey((k) => k + 1); setMobileVisible(true); }, 240);
  }, []);

  useEffect(() => {
    if (isHovering) return;
    const id = setInterval(() => {
      setActive((prev) => { const next = (prev + 1) % deepDives.length; setProgressKey((k) => k + 1); return next; });
    }, AUTO_INTERVAL);
    return () => clearInterval(id);
  }, [isHovering, deepDives.length]);

  const item = deepDives[active];

  return (
    <section className="w-full bg-stone-50 pt-12 lg:pt-16 pb-24 lg:pb-32 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <div ref={headerRef} className="mb-10">
          <p className="font-label text-xs tracking-[0.3em] text-stone-400 uppercase mb-3" style={{ opacity: hv ? 1 : 0, transform: hv ? 'translateY(0)' : 'translateY(16px)', transition: 'opacity 0.9s ease, transform 0.9s cubic-bezier(0.16,1,0.3,1)' }}>{t('dd_label')}</p>
          <div className="overflow-hidden">
            <h2 className="font-zh-serif text-3xl lg:text-4xl text-stone-900 font-light" style={{ display: 'block', transform: hv ? 'translateY(0)' : 'translateY(110%)', transition: 'transform 1.1s cubic-bezier(0.16,1,0.3,1) 160ms' }}>{t('dd_title')}</h2>
          </div>
        </div>

        {/* Desktop accordion */}
        <div className="hidden lg:flex gap-2 rounded-2xl overflow-hidden" style={{ height: '540px' }} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
          {deepDives.map((d, i) => {
            const isActive = i === active;
            return (
              <div key={d.index} className="relative overflow-hidden rounded-xl cursor-pointer" style={{ flex: isActive ? '3 1 0%' : '0.65 1 0%', minWidth: '72px', transition: 'flex 0.78s cubic-bezier(0.4,0,0.2,1)' }} onClick={() => !isActive && switchTo(i)}>
                <img src={d.image} alt={d.title} className="absolute inset-0 w-full h-full object-cover pointer-events-none" style={{ transform: isActive ? 'scale(1.02)' : 'scale(1.1)', transition: 'transform 0.78s cubic-bezier(0.4,0,0.2,1)' }} loading="lazy" decoding="async" draggable={false} />
                <div className="absolute inset-0" style={{ background: isActive ? 'linear-gradient(to top,rgba(0,0,0,0.82) 0%,rgba(0,0,0,0.28) 45%,rgba(0,0,0,0.06) 100%)' : 'linear-gradient(to top,rgba(0,0,0,0.65) 0%,rgba(0,0,0,0.45) 100%)', transition: 'background 0.6s ease' }} />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 pointer-events-none" style={{ opacity: isActive ? 0 : 1, transition: 'opacity 0.35s ease' }}>
                  <span className="font-label text-white/50 text-[10px] tracking-[0.3em]" style={{ writingMode: 'vertical-rl' }}>{d.index}</span>
                  <div className="w-px h-10 bg-white/25" />
                  <span className="font-label text-white/90 text-[11px] tracking-[0.2em] uppercase" style={{ writingMode: 'vertical-rl' }}>{d.category}</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-10 pointer-events-none" style={{ opacity: isActive ? 1 : 0, transform: isActive ? 'translateY(0)' : 'translateY(20px)', transition: isActive ? 'opacity 0.45s ease 0.3s,transform 0.45s ease 0.3s' : 'opacity 0.2s ease,transform 0.2s ease' }}>
                  <span className="font-label text-[10px] tracking-[0.3em] text-white/60 uppercase mb-4 block">{d.index} · {d.category}</span>
                  <h3 className="font-zh-serif text-2xl xl:text-3xl text-white font-light leading-snug mb-5">{d.title}</h3>
                  <div className="pl-4 border-l-2 border-white/35 mb-5"><p className="font-noto text-white/80 text-sm leading-7 font-light">{d.quote}</p></div>
                  <p className="font-zh-serif text-white text-sm leading-7 font-medium mb-7">{d.highlight}</p>
                  <div className="pt-5 border-t border-white/20 flex items-end justify-between">
                    <div>
                      <p className={`font-display ${(d.stat as { value: string; desc: string; valueClass?: string }).valueClass ?? 'text-5xl'} text-white font-light leading-none`}>{d.stat.value}</p>
                      <p className="font-label text-white/55 text-[10px] tracking-widest mt-1.5 uppercase">{d.stat.desc}</p>
                    </div>
                    <div className="flex items-center gap-2.5 pointer-events-auto">
                      {deepDives.map((_, j) => (
                        <button key={j} onClick={(e) => { e.stopPropagation(); switchTo(j); }} className={`rounded-full transition-all duration-300 cursor-pointer ${j === active ? 'w-7 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/70'}`} aria-label={deepDives[j].label} />
                      ))}
                    </div>
                  </div>
                </div>
                {isActive && !isHovering && (
                  <div className="absolute bottom-0 left-0 right-0 h-[3px] overflow-hidden pointer-events-none">
                    <div key={progressKey} className="h-full bg-white/50 rounded-full" style={{ animation: `panelFill ${AUTO_INTERVAL}ms linear forwards` }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Mobile */}
        <div className="lg:hidden" onTouchStart={() => setIsHovering(true)} onTouchEnd={() => setIsHovering(false)}>
          <div className="flex border-b border-stone-200 mb-0">
            {deepDives.map((d, i) => (
              <button key={d.index} onClick={() => i !== active && switchTo(i)} className={`relative flex items-center gap-2 px-4 py-3.5 cursor-pointer transition-colors duration-200 whitespace-nowrap group ${active === i ? 'text-stone-900' : 'text-stone-400 hover:text-stone-600'}`}>
                <span className="font-label text-[10px] tracking-[0.2em] hidden sm:inline">{d.index}</span>
                <span className="font-noto text-sm font-medium">{d.label}</span>
                <span className={`absolute bottom-0 left-0 h-[2px] bg-stone-900 transition-all duration-300 ${active === i ? 'w-full' : 'w-0 group-hover:w-1/2'}`} />
              </button>
            ))}
          </div>
          <div className={`overflow-hidden rounded-b-xl transition-all duration-240 ${mobileVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="relative w-full overflow-hidden" style={{ height: '260px' }}>
              <img src={item.image} alt={item.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" draggable={false} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              <div className="absolute top-4 left-4"><span className="font-label text-[10px] tracking-[0.25em] text-white/80 uppercase bg-black/35 backdrop-blur-sm px-3 py-1.5 rounded-sm">{item.index} · {item.category}</span></div>
              <div className="absolute bottom-5 left-5">
                <p className={`font-display ${(item.stat as { value: string; desc: string; valueClass?: string }).valueClass ?? 'text-5xl'} text-white font-light leading-none`}>{item.stat.value}</p>
                <p className="font-label text-white/65 text-[10px] tracking-widest mt-1 uppercase">{item.stat.desc}</p>
              </div>
            </div>
            <div className="bg-white px-6 py-8">
              <p className="font-label text-[10px] tracking-[0.3em] text-stone-400 uppercase mb-3">PILLAR {item.index}</p>
              <h3 className="font-zh-serif text-2xl text-stone-900 font-light leading-snug mb-5">{item.title}</h3>
              <div className="pl-4 border-l-2 border-stone-300 mb-4"><p className="font-noto text-stone-500 text-sm leading-7 font-light">{item.quote}</p></div>
              <p className="font-zh-serif text-stone-800 text-sm leading-7 font-medium mb-6">{item.highlight}</p>
              <div className="flex items-center gap-2.5 pt-5 border-t border-stone-100">
                {deepDives.map((_, j) => (
                  <button key={j} onClick={() => j !== active && switchTo(j)} className={`rounded-full transition-all duration-300 cursor-pointer ${j === active ? 'w-7 h-1.5 bg-stone-900' : 'w-1.5 h-1.5 bg-stone-300 hover:bg-stone-500'}`} aria-label={deepDives[j].label} />
                ))}
                <span className="ml-2 font-label text-[10px] tracking-widest text-stone-400">{active + 1} / {deepDives.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes panelFill { from { width: 0% } to { width: 100% } }`}</style>
    </section>
  );
}
